import { GetTaskInboxRequest, TaskInbox } from '../../../models/link/Task';
import { parseResponseError } from '../../../common/helpers';
import { DatasourceReturnType, ResponseError, ResponseList } from '../../../models';
import {
    getAll,
    reqestTaskInbox,
    requestCancelTask,
    requestCancelTaskV2,
    requestCreateTaskRemarkV2,
    requestCloseTask,
    requestCloseTaskV2,
    requestLinkTask,
    requestTaskActivity,
    requestTaskActivityV2,
    requestTaskInboxByLinkId,
    requestUpdateTaskProgressV2,
    requestUserAccountDataByEmailV2
} from '../datasources/task.datasource';
import {
    CreateTaskRemarkResponse,
    GetTaskSchedulResponse,
    GetTaskScheduleRequest,
    LinkMapping,
    TaskActivity,
    TaskActivityRequest
} from '../schemas';
import { Task } from '../../../models/link/Link';

const TASK_INBOX_FILTERS = ['main_delegate', 'my_assignment'] as const;

type TaskInboxFilter = (typeof TASK_INBOX_FILTERS)[number];
type TaskInboxProgressHandler = (tasks: TaskInbox[]) => void;

export async function getTasksSchedule(
    params: GetTaskScheduleRequest
): Promise<DatasourceReturnType<ResponseError, ResponseList<LinkMapping>>> {
    try {
        const data = (await getAll(params)).data;
        //console.log('[Debug] getAll success', data);

        return [null!, data];
    } catch (error) {
        //console.log('[Debug] getAll error', error);
        const reason = parseResponseError(error);

        if (reason) {
            return [reason, null!];
        }

        throw error;
    }
}

export async function getTaskActivity(
    params: TaskActivityRequest
): Promise<DatasourceReturnType<ResponseError, TaskActivity>> {
    try {
        const data = (await requestTaskActivity(params)).data;

        return [null!, data];
    } catch (error) {
        const reason = parseResponseError(error);

        if (reason) {
            return [reason, null!];
        }

        throw error;
    }
}

export async function getTaskActivityV2(
    params: TaskActivityRequest
): Promise<DatasourceReturnType<ResponseError, TaskActivity>> {
    try {
        const data = (await requestTaskActivityV2(params)).data;
        return [null!, data];
    } catch (error) {
        const reason = parseResponseError(error);
        if (reason) return [reason, null!];
        throw error;
    }
}

export async function getTaskInbox(
    params: string
): Promise<DatasourceReturnType<ResponseError, ResponseList<TaskInbox>>> {
    try {
        const data = (await reqestTaskInbox(params)).data;

        return [null!, data];
    } catch (error) {
        const reason = parseResponseError(error);

        if (reason) {
            return [reason, null!];
        }

        throw error;
    }
}

export async function getTaskInboxProgressivelyByLinkId(
    linkId: string,
    onProgress: TaskInboxProgressHandler,
    signal?: AbortSignal
): Promise<DatasourceReturnType<ResponseError, TaskInbox[]>> {
    try {
        const tasksByKey = new Map<string, TaskInbox>();

        await Promise.all(
            TASK_INBOX_FILTERS.map(filter =>
                loadTaskInboxFilter(linkId, filter, tasksByKey, onProgress, signal)
            )
        );

        return [null!, sortTaskInbox(Array.from(tasksByKey.values()))];
    } catch (error) {
        const reason = parseResponseError(error);

        if (reason) {
            return [reason, null!];
        }

        throw error;
    }
}

async function loadTaskInboxFilter(
    linkId: string,
    filter: TaskInboxFilter,
    tasksByKey: Map<string, TaskInbox>,
    onProgress: TaskInboxProgressHandler,
    signal?: AbortSignal
) {
    const filterTaskKeys = new Set<string>();

    while (true) {
        const body: GetTaskInboxRequest = filterTaskKeys.size
            ? { exclude_key: Array.from(filterTaskKeys).join(',') }
            : {};
        const response = (await requestTaskInboxByLinkId(linkId, filter, body, signal)).data;
        const pageTasks = response.result_list ?? [];
        const newFilterTasks = pageTasks.filter(task => task.key && !filterTaskKeys.has(task.key));

        newFilterTasks.forEach(task => {
            filterTaskKeys.add(task.key);
            tasksByKey.set(task.key, task);
        });

        onProgress(sortTaskInbox(Array.from(tasksByKey.values())));

        if (!pageTasks.length || !newFilterTasks.length) {
            break;
        }
    }
}

function sortTaskInbox(tasks: TaskInbox[]) {
    return tasks.sort((first, second) =>
        `${second.checkin_date ?? ''}${second.checkin_time ?? ''}`.localeCompare(
            `${first.checkin_date ?? ''}${first.checkin_time ?? ''}`
        )
    );
}

export async function getTaskLink(
    params: string
): Promise<DatasourceReturnType<ResponseError, Task[]>> {
    try {
        const data = (await requestLinkTask(params)).data;

        return [null!, data];
    } catch (error) {
        const reason = parseResponseError(error);

        if (reason) {
            return [reason, null!];
        }

        throw error;
    }
}

export async function getUserAccountDataByEmailV2(
    email: string
): Promise<DatasourceReturnType<ResponseError, string>> {
    try {
        const data = (await requestUserAccountDataByEmailV2(email)).data;
        return [null!, data];
    } catch (error) {
        const reason = parseResponseError(error);
        if (reason) return [reason, null!];
        throw error;
    }
}

export async function closeTaskActivity(
    object_link: string,
    link_id?: string
): Promise<DatasourceReturnType<ResponseError, { message: string }>> {
    try {
        const data = (await requestCloseTask(object_link, link_id)).data;

        return [null!, data];
    } catch (error) {
        const reason = parseResponseError(error);

        if (reason) {
            return [reason, null!];
        }

        throw error;
    }
}

export async function cancelTaskActivity(
    object_link: string,
    link_id?: string
): Promise<DatasourceReturnType<ResponseError, { message: string }>> {
    try {
        const data = (await requestCancelTask(object_link, link_id)).data;

        return [null!, data];
    } catch (error) {
        const reason = parseResponseError(error);

        if (reason) {
            return [reason, null!];
        }

        throw error;
    }
}

export async function closeTaskActivityV2(
    objectLink: string,
    linkId: string
): Promise<DatasourceReturnType<ResponseError, { message: string }>> {
    try {
        return [null!, (await requestCloseTaskV2(objectLink, linkId)).data];
    } catch (error) {
        const reason = parseResponseError(error);
        if (reason) return [reason, null!];
        throw error;
    }
}

export async function cancelTaskActivityV2(
    objectLink: string,
    linkId: string
): Promise<DatasourceReturnType<ResponseError, { message: string }>> {
    try {
        return [null!, (await requestCancelTaskV2(objectLink, linkId)).data];
    } catch (error) {
        const reason = parseResponseError(error);
        if (reason) return [reason, null!];
        throw error;
    }
}

export async function updateTaskProgressV2(
    objectLink: string,
    linkId: string,
    code: string
): Promise<DatasourceReturnType<ResponseError, { message: string }>> {
    try {
        return [null!, (await requestUpdateTaskProgressV2(objectLink, linkId, code)).data];
    } catch (error) {
        const reason = parseResponseError(error);
        if (reason) return [reason, null!];
        throw error;
    }
}

export async function createTaskRemarkV2(
    objectLink: string,
    message: string
): Promise<DatasourceReturnType<ResponseError, CreateTaskRemarkResponse>> {
    try {
        return [null!, (await requestCreateTaskRemarkV2(objectLink, { message })).data];
    } catch (error) {
        const reason = parseResponseError(error);
        if (reason) return [reason, null!];
        throw error;
    }
}
